/*
 * Tier discount codes – cart display for MANUAL Shopify discount codes.
 *
 * Liquid (snippets/tier-discount-candidates.liquid) works out which tiers the
 * cart qualifies for and renders a hidden "discounted" version of the totals
 * for each one. This script checks with Shopify whether each code is live
 * (exists, active, and applicable to this cart) and only then reveals the
 * discounted totals. Codes that are paused, expired, deleted or not
 * applicable are never shown.
 *
 * How a code is checked: the code is applied to the cart with
 * POST /cart/update.js { discount: CODE }, Shopify's answer
 * (discount_codes[].applicable) is read, and the cart's previous codes are put
 * straight back. The code is NOT left on the cart – the checkout applies it.
 * Results are cached in localStorage (positive: settings.cacheMinutes,
 * negative: max 5 minutes and only for the same subtotal).
 *
 * Markup contract:
 *   [data-td-scope]                    container (cart drawer / cart page)
 *   script[data-td-config]             {"subtotal":123400,"candidates":[{"code":"X","amount":30000}]}
 *   [data-td-hide]                     visible only when NO tier code is active
 *   [data-td-show="CODE1 CODE2"]       visible only when one of these codes is active
 */
(function () {
  if (window.__tierDiscountLoaded) return;
  window.__tierDiscountLoaded = true;

  var S = window.tierDiscountSettings || {};
  var CART_URL = (S.cartUrl || '/cart').replace(/\/$/, '');
  var ROOT_URL = S.rootUrl || '/';
  if (ROOT_URL.slice(-1) !== '/') ROOT_URL += '/';
  var POSITIVE_TTL = Math.max(1, Number(S.cacheMinutes) || 10) * 60 * 1000;
  var NEGATIVE_TTL = Math.min(POSITIVE_TTL, 5 * 60 * 1000);
  var FALLBACK = S.fallback !== false;
  var LS_PREFIX = 'tdv1:';
  var memoryCache = {};

  /* ---------- cache ---------- */
  function cacheGet(key) {
    var now = Date.now();
    var mem = memoryCache[key];
    if (mem && mem.e > now) return mem.v;
    try {
      var raw = window.localStorage.getItem(LS_PREFIX + key);
      if (!raw) return undefined;
      var o = JSON.parse(raw);
      if (!o || o.e <= now) {
        window.localStorage.removeItem(LS_PREFIX + key);
        return undefined;
      }
      memoryCache[key] = o;
      return o.v;
    } catch (e) {
      return undefined;
    }
  }
  function cacheSet(key, value, ttl) {
    var o = { v: value, e: Date.now() + ttl };
    memoryCache[key] = o;
    try {
      window.localStorage.setItem(LS_PREFIX + key, JSON.stringify(o));
    } catch (e) {}
  }
  function cachedStatus(code, subtotal) {
    if (cacheGet('live:' + code) === 1) return true;
    if (cacheGet('dead:' + code + ':' + subtotal) === 1) return false;
    return undefined;
  }

  /* ---------- Shopify cart API ---------- */
  function getCart() {
    return fetch(CART_URL + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }
  function setCartDiscounts(value) {
    return fetch(CART_URL + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: value }),
    }).then(function (r) {
      if (!r.ok) throw new Error('update.js ' + r.status);
      return r.json();
    });
  }
  function same(a, b) {
    return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
  }
  function isApplicable(cart, code) {
    if (!cart) return false;
    if (Array.isArray(cart.discount_codes)) {
      for (var i = 0; i < cart.discount_codes.length; i++) {
        var d = cart.discount_codes[i];
        if (same(d.code, code)) return d.applicable === true;
      }
      return false;
    }
    // Older response shape: look for an applied *code* (not an automatic discount) with this title.
    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      if (apps[j].type === 'discount_code' && (same(apps[j].title, code) || same(apps[j].code, code))) return true;
    }
    return false;
  }

  /* ---------- verification (serialised: one cart mutation at a time) ---------- */
  var queue = Promise.resolve();
  var inflight = {};
  var testing = 0;
  var renderedDuringTest = false;
  var lastRenderAt = 0;

  function verify(code, subtotal) {
    var key = code + ':' + subtotal;
    if (inflight[key]) return inflight[key];
    var p = (queue = queue.then(function () {
      return doVerify(code, subtotal);
    }, function () {
      return doVerify(code, subtotal);
    }));
    inflight[key] = p;
    p.then(clear, clear);
    function clear() {
      delete inflight[key];
    }
    return p;
  }

  function doVerify(code, subtotal) {
    var known = cachedStatus(code, subtotal);
    if (known !== undefined) return Promise.resolve(known);

    return getCart()
      .then(function (before) {
        var codes = Array.isArray(before.discount_codes) ? before.discount_codes : [];
        for (var i = 0; i < codes.length; i++) {
          if (same(codes[i].code, code)) return codes[i].applicable === true;
        }
        var previous = codes
          .map(function (d) {
            return d.code;
          })
          .filter(Boolean)
          .join(',');

        testing++;
        renderedDuringTest = false;
        var result = false;
        return setCartDiscounts(code)
          .then(function (after) {
            result = isApplicable(after, code);
          })
          .then(restore, function (err) {
            return restore().then(function () {
              throw err;
            });
          })
          .then(function () {
            return result;
          });

        function restore() {
          return setCartDiscounts(previous)
            .catch(function () {
              return setCartDiscounts(previous); // one retry
            })
            .catch(function () {})
            .then(function () {
              testing--;
              var restoredAt = Date.now();
              if (renderedDuringTest) {
                // Something re-rendered the cart while the test code was on it.
                // Give the theme a moment; if nothing re-renders, refresh ourselves.
                setTimeout(function () {
                  if (lastRenderAt < restoredAt) refreshCartSections();
                }, 700);
              }
            });
        }
      })
      .then(function (ok) {
        if (ok) cacheSet('live:' + code, 1, POSITIVE_TTL);
        else cacheSet('dead:' + code + ':' + subtotal, 1, NEGATIVE_TTL);
        return ok;
      })
      .catch(function () {
        return null; // network/API problem: unknown, show nothing, don't cache
      });
  }

  function refreshCartSections() {
    var drawerInner = document.getElementById('CartDrawer');
    if (drawerInner) {
      fetch(ROOT_URL + '?sections=cart-drawer', { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (json) {
          var html = json && json['cart-drawer'];
          if (!html) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('CartDrawer');
          if (fresh) drawerInner.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }
    var footer = document.getElementById('main-cart-footer');
    if (footer && footer.dataset.id) {
      fetch(CART_URL + '?sections=' + encodeURIComponent(footer.dataset.id), { credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (json) {
          var html = json && json[footer.dataset.id];
          if (!html) return;
          var fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('main-cart-footer');
          if (fresh) footer.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }
  }

  /* ---------- deciding + showing ---------- */
  function resolveSync(candidates, subtotal) {
    for (var i = 0; i < candidates.length; i++) {
      var s = cachedStatus(candidates[i].code, subtotal);
      if (s === true) return { done: true, code: candidates[i].code };
      if (s === undefined) return { done: false };
      if (!FALLBACK) return { done: true, code: null };
    }
    return { done: true, code: null };
  }

  function resolveAsync(candidates, subtotal) {
    var i = 0;
    function next() {
      if (i >= candidates.length) return Promise.resolve(null);
      var c = candidates[i++];
      return verify(c.code, subtotal).then(function (ok) {
        if (ok === true) return c.code;
        if (ok === null) return null; // couldn't check: be safe, show nothing
        return FALLBACK ? next() : null;
      });
    }
    return next();
  }

  function setActive(scope, code, candidates) {
    var els = scope.querySelectorAll('[data-td-show], [data-td-hide]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var show;
      if (el.hasAttribute('data-td-hide')) {
        show = !code;
      } else {
        show = !!code && (' ' + el.getAttribute('data-td-show') + ' ').indexOf(' ' + code + ' ') !== -1;
      }
      el.classList.toggle('td-hidden', !show);
    }
    var prev = scope.getAttribute('data-td-active') || '';
    scope.setAttribute('data-td-active', code || '');
    if (prev !== (code || '')) {
      var amount = 0;
      (candidates || []).forEach(function (c) {
        if (c.code === code) amount = c.amount;
      });
      document.dispatchEvent(new CustomEvent('tier-discount:change', { detail: { code: code || null, amount: amount, scope: scope } }));
    }
  }

  function applyConfig(configEl) {
    var cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    var scope = configEl.closest('[data-td-scope]');
    if (!scope) return;
    var candidates = (cfg && cfg.candidates) || [];
    var subtotal = cfg.subtotal;
    if (!candidates.length) {
      setActive(scope, null, candidates);
      return;
    }
    var sync = resolveSync(candidates, subtotal);
    if (sync.done) {
      setActive(scope, sync.code, candidates);
      return;
    }
    setActive(scope, null, candidates);
    resolveAsync(candidates, subtotal).then(function (code) {
      if (configEl.isConnected) setActive(scope, code, candidates);
    });
  }

  function scan() {
    var scopes = document.querySelectorAll('[data-td-scope]');
    for (var i = 0; i < scopes.length; i++) {
      var scope = scopes[i];
      var configs = scope.querySelectorAll('script[data-td-config]');
      if (configs.length) applyConfig(configs[configs.length - 1]);
      else setActive(scope, null, []);
    }
  }

  /* Re-apply whenever the theme re-renders cart markup (runs before paint). */
  var scheduled = false;
  var observer = new MutationObserver(function (mutations) {
    var relevant = false;
    for (var i = 0; i < mutations.length && !relevant; i++) {
      var t = mutations[i].target;
      if (t.nodeType === 1 && (t.closest('[data-td-scope]') || t.querySelector('[data-td-scope]'))) relevant = true;
    }
    if (!relevant) return;
    lastRenderAt = Date.now();
    if (testing > 0) renderedDuringTest = true;
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(function () {
      scheduled = false;
      scan();
    });
  });

  /* Shopify checkout (non-Shiprocket visitors): carry the active code into checkout. */
  document.addEventListener('submit', function (e) {
    if (!S.applyOnShopifyCheckout || e.defaultPrevented) return;
    var btn = e.submitter;
    if (!btn || btn.name !== 'checkout') return;
    var scope = btn.closest('[data-td-scope]') || (e.target && e.target.closest && e.target.closest('[data-td-scope]'));
    if (!scope) {
      var all = document.querySelectorAll('[data-td-scope][data-td-active]');
      for (var i = 0; i < all.length; i++) if (all[i].getAttribute('data-td-active')) scope = all[i];
    }
    var code = scope && scope.getAttribute('data-td-active');
    if (!code) return;
    e.preventDefault();
    window.location.href = ROOT_URL + 'checkout?discount=' + encodeURIComponent(code);
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    scan();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.TierDiscount = {
    rescan: scan,
    clearCache: function () {
      memoryCache = {};
      try {
        Object.keys(window.localStorage).forEach(function (k) {
          if (k.indexOf(LS_PREFIX) === 0) window.localStorage.removeItem(k);
        });
      } catch (e) {}
      scan();
    },
  };
})();
